import { SunnyRequestBuilder } from '@/models/Common/SunnyRequestBuilder';
import IMemberSocialFindRequestParams from '@/models/Member/interfaces/IMemberSocialFindRequestParams';
import IMemberSocialJoinRequestParams from '@/models/Member/interfaces/IMemberSocialJoinRequestParams';
import IMemberSocialLinkInfoRequestParams from '@/models/Member/interfaces/IMemberSocialLinkInfoRequestParams';
import IMemberSocialLinkRequestParams from '@/models/Member/interfaces/IMemberSocialLinkRequestParams';
import { IMemberSocialLinkValidateRequestParams } from '@/models/Member/interfaces/IMemberSocialLinkValidateRequestParams';
import IMemberSocialLoginV2RequestParams from '@/models/Member/interfaces/IMemberSocialLoginV2RequestParams';
import IMemberSocialLoginWithLinkRequestParams from '@/models/Member/interfaces/IMemberSocialLoginWithLinkRequestParams';
import IMemberSocialUnlinkRequestParams from '@/models/Member/interfaces/IMemberSocialUnlinkRequestParams';
import MemberChangeAgreeJSC from '@/schema/MemberChangeAgreeJSC';
import MemberChangeEmailJSC from '@/schema/MemberChangeEmailJSC';
import MemberChangeNickNameJSC from '@/schema/MemberChangeNickNameJSC';
import MemberChangePasswordJSC from '@/schema/MemberChangePasswordJSC';
import MemberCheckMilkConnectionJSC from '@/schema/MemberCheckMilkConnectionJSC';
import MemberConfirmPasswordJSC from '@/schema/MemberConfirmPasswordJSC';
import MemberLiveCommerceTokenJSC from '@/schema/MemberLiveCommerceTokenJSC';
import MemberMilkConnectJSC from '@/schema/MemberMilkConnectJSC';
import MemberMilkDisconnectJSC from '@/schema/MemberMilkDisconnectJSC';
import MemberMyBenefitJSC from '@/schema/MemberMyBenefitJSC';
import MemberSocialFindJSC from '@/schema/MemberSocialFindJSC';
import MemberSocialJoinJSC from '@/schema/MemberSocialJoinJSC';
import MemberSocialLinkInfoJSC from '@/schema/MemberSocialLinkInfoJSC';
import MemberSocialLinkJSC from '@/schema/MemberSocialLinkJSC';
import MemberSocialLinkValidateJSC from '@/schema/MemberSocialLinkValidateJSC';
import MemberSocialLoginV2JSC from '@/schema/MemberSocialLoginV2JSC';
import MemberSocialLoginWithLinkJSC from '@/schema/MemberSocialLoginWithLinkJSC';
import MemberSocialUnlinkJSC from '@/schema/MemberSocialUnlinkJSC';
import MemberUnsubscribeJSC from '@/schema/MemberUnsubscribeJSC';
import MemberWithdrawJSC from '@/schema/MemberWithdrawJSC';
import MemberWithdrawReasonListJSC from '@/schema/MemberWithdrawReasonListJSC';
import { TReqConfigValidated } from '@/services/JSCService';

import { IMemberChangeAgreeRequestParams } from './interfaces/IMemberChangeAgreeRequestParams';
import { IMemberChangeEmailRequestParams } from './interfaces/IMemberChangeEmailRequestParams';
import { IMemberChangeNickNameRequestParams } from './interfaces/IMemberChangeNickNameRequestParams';
import { IMemberChangePasswordRequestParams } from './interfaces/IMemberChangePasswordRequestParams';
import { IMemberCheckMilkConnectionRequestParams } from './interfaces/IMemberCheckMilkConnectionRequestParams';
import { IMemberConfirmPasswordRequestParams } from './interfaces/IMemberConfirmPasswordRequestParams';
import { IMemberLiveCommerceTokenRequestParams } from './interfaces/IMemberLiveCommerceTokenRequestParams';
import { IMemberMilkConnectRequestParams } from './interfaces/IMemberMilkConnectRequestParams';
import { IMemberMilkDisconnectRequestParams } from './interfaces/IMemberMilkDisconnectRequestParams';
import { IMemberMyBenefitRequestParams } from './interfaces/IMemberMyBenefitRequestParams';
import { IMemberUnsubscribeRequestParams } from './interfaces/IMemberUnsubscribeRequestParams';
import { IMemberWithdrawReasonListRequestParams } from './interfaces/IMemberWithdrawReasonListRequestParams';
import { IMemberWithdrawRequestParams } from './interfaces/IMemberWithdrawRequestParams';

export default class MemberRequestBuilder extends SunnyRequestBuilder {
  createPutChangeNickName(reqParams: IMemberChangeNickNameRequestParams): TReqConfigValidated {
    return this.validate(MemberChangeNickNameJSC, reqParams).map((params) =>
      this.makeConfig('PUT', '/member/change_nickname', params),
    );
  }

  createPostConfirmPassword(reqParams: IMemberConfirmPasswordRequestParams): TReqConfigValidated {
    return this.validate(MemberConfirmPasswordJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/check_current_password', params),
    );
  }

  createPutChangePassword(reqParams: IMemberChangePasswordRequestParams): TReqConfigValidated {
    return this.validate(MemberChangePasswordJSC, reqParams).map((params) =>
      this.makeConfig('PUT', '/member/update_password_for_mweb', params),
    );
  }

  createPostChangeEmail(reqParams: IMemberChangeEmailRequestParams): TReqConfigValidated {
    return this.validate(MemberChangeEmailJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/reset/send_email', params),
    );
  }

  createPostChangeAgree(reqParams: IMemberChangeAgreeRequestParams): TReqConfigValidated {
    return this.validate(MemberChangeAgreeJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/agree/save', params),
    );
  }

  createPostWithdraw(reqParams: IMemberWithdrawRequestParams): TReqConfigValidated {
    return this.validate(MemberWithdrawJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/withdraw', params),
    );
  }

  createGetWithdrawReasonList(reqParams: IMemberWithdrawReasonListRequestParams): TReqConfigValidated {
    return this.validate(MemberWithdrawReasonListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/member/withdraw_reasons', params),
    );
  }

  createGetCheckMilkConnection(reqParams?: IMemberCheckMilkConnectionRequestParams): TReqConfigValidated {
    return this.validate(MemberCheckMilkConnectionJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/member/milk/link', params, { timeout: 8000 }),
    );
  }

  createPostConnectWithMilk(reqParams: IMemberMilkConnectRequestParams): TReqConfigValidated {
    return this.validate(MemberMilkConnectJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/milk/link', params, { timeout: 8000 }),
    );
  }

  createPostDisconnectMilk(reqParams?: IMemberMilkDisconnectRequestParams): TReqConfigValidated {
    return this.validate(MemberMilkDisconnectJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/milk/unlink', params, { timeout: 8000 }),
    );
  }

  createPostUnsubscribe(reqParams?: IMemberUnsubscribeRequestParams): TReqConfigValidated {
    return this.validate(MemberUnsubscribeJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/unsubscribe', params, { timeout: 8000 }),
    );
  }

  createGetMyBenefit(reqParams: IMemberMyBenefitRequestParams): TReqConfigValidated {
    return this.validate(MemberMyBenefitJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/member/benefit', params),
    );
  }

  createPostSocialFind(reqParams: IMemberSocialFindRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialFindJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/find/v2', params),
    );
  }

  createPostSocialLink(reqParams: IMemberSocialLinkRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialLinkJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/link', params),
    );
  }

  createDeleteSocialUnlink(reqParams: IMemberSocialUnlinkRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialUnlinkJSC, reqParams).map((params) =>
      this.makeConfig('DELETE', '/member/social/link', params),
    );
  }

  createPostSocialLogin(reqParams: IMemberSocialLoginV2RequestParams): TReqConfigValidated {
    return this.validate(MemberSocialLoginV2JSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/login', params),
    );
  }

  createPostSocialJoin(reqParams: IMemberSocialJoinRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialJoinJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/join', params),
    );
  }

  createPostSocialLoginWithLink(reqParams: IMemberSocialLoginWithLinkRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialLoginWithLinkJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/login_with_link', params),
    );
  }

  createGetSocialLinkInfo(reqParams: IMemberSocialLinkInfoRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialLinkInfoJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/member/social/link/info', params),
    );
  }

  createPostSocialLinkValidate(reqParams: IMemberSocialLinkValidateRequestParams): TReqConfigValidated {
    return this.validate(MemberSocialLinkValidateJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/member/social/link/validate', params),
    );
  }

  createPostLiveCommerceToken(reqParms: IMemberLiveCommerceTokenRequestParams): TReqConfigValidated {
    return this.validate(MemberLiveCommerceTokenJSC, reqParms).map((params) =>
      this.makeConfig('POST', '/member/live_commerce/token', params),
    );
  }
}
